import React, { useState, useCallback, useMemo } from 'react';
import {
  ContentLayout,
  Container,
  Header,
  SpaceBetween,
  Alert,
  Button,
} from '@amzn/awsui-components-console';
import SearchBar from './components/SearchBar';
import SavedSearchesBar from './components/SavedSearchesBar';
import SearchTypeToggle from './components/SearchTypeToggle';
import SearchFilterBar from './components/SearchFilterBar';
import SearchResultsTable from './components/SearchResultsTable';
import InfoTooltip from 'src/common/ui/InfoTooltip';
import { SearchFilters } from 'src/api/graphql/searchTypes';
import {
  hasActiveFilters,
  hasMinimumSearchCriteria,
  NO_FILTERS,
} from './searchFilterUtils';
import {
  DEFAULT_SORT_STATE,
  removeSourceOnlySortField,
  type SortState,
} from './sortState';

export type SearchMode = 'SOURCE' | 'TRANSLATION';

/** Named constants for SearchMode values — avoids scattering string literals. */
export const SEARCH_MODE = {
  SOURCE: 'SOURCE',
  TRANSLATION: 'TRANSLATION',
} as const satisfies Record<string, SearchMode>;

export interface SearchState {
  queryText: string;
  mode: SearchMode;
  exactMatch: boolean;
  filters: SearchFilters;
  /**
   * The sort in effect, or `null` for "no explicit sort" — the default. Whatever
   * it holds is sent to the backend with every search, and that is the ONLY
   * thing that orders the rows; there is no client-side re-sort. When it is
   * `null` the request carries no `sortBy`/`sortOrder` at all and the backend
   * orders by relevance (keyword search) or recency (browse). See `sortState.ts`.
   */
  sortState: SortState;
}

const DEFAULT_SEARCH_STATE: SearchState = {
  queryText: '',
  mode: SEARCH_MODE.SOURCE,
  exactMatch: false,
  filters: NO_FILTERS,
  sortState: DEFAULT_SORT_STATE,
};

const SEARCH_HELP_TEXT =
  'Search across source strings and translations in namespaces you have access to. ' +
  'Use filters to narrow results by namespace, family, string group, or status.';

/**
 * Whether this state may be submitted as a search.
 *
 * The minimum-criteria rule is scoped to SOURCE mode on purpose. Translation
 * mode has its own, pre-existing required-filter rule (`translatedLocales`,
 * enforced by the backend and re-checked in `SearchResultsTable.doSearch`);
 * extending this gate to it would change that behaviour, which is out of scope
 * here.
 */
const meetsSearchCriteria = (state: SearchState): boolean =>
  state.mode !== SEARCH_MODE.SOURCE ||
  hasMinimumSearchCriteria(state.filters, state.queryText);

const SearchPage: React.FC = () => {
  const [searchState, setSearchState] =
    useState<SearchState>(DEFAULT_SEARCH_STATE);
  const [triggerSearch, setTriggerSearch] = useState(0);

  const handleSearch = useCallback(() => {
    setTriggerSearch((prev) => prev + 1);
  }, []);

  const handleQueryChange = useCallback((queryText: string) => {
    setSearchState((prev) => ({ ...prev, queryText }));
  }, []);

  const handleModeChange = useCallback((mode: SearchMode) => {
    setSearchState((prev) => {
      const newState = { ...prev, mode };
      // Clear source-only filters and the sort criterion when switching to
      // translation mode
      if (mode === SEARCH_MODE.TRANSLATION) {
        newState.filters = { ...prev.filters, statuses: undefined };
        newState.sortState = removeSourceOnlySortField(prev.sortState);
      }
      // Clear translation-only filters when switching to source mode
      if (mode === SEARCH_MODE.SOURCE) {
        newState.filters = { ...prev.filters, translatedLocales: undefined };
      }
      return newState;
    });
  }, []);

  const handleExactMatchChange = useCallback((exactMatch: boolean) => {
    setSearchState((prev) => ({ ...prev, exactMatch }));
  }, []);

  const handleFiltersChange = useCallback(
    (update: SearchFilters | ((prev: SearchFilters) => SearchFilters)) => {
      setSearchState((prev) => ({
        ...prev,
        filters: typeof update === 'function' ? update(prev.filters) : update,
      }));
    },
    [],
  );

  const handleSortChange = useCallback((sortState: SortState) => {
    setSearchState((prev) => ({ ...prev, sortState }));
  }, []);

  const handleLoadSavedSearch = useCallback((saved: SearchState) => {
    setSearchState(saved);
    /*
     * Loading a saved search is a THIRD submit path (button and Enter are the
     * other two) and has to honour the same gate. Saved searches are only
     * offered when something is set (`canSave` in SearchBar), but the payload
     * comes from localStorage — hand-edited, or written by an older build with
     * different rules — so an empty-criteria entry must not auto-fire an
     * unbounded query. The state is still loaded either way; only the automatic
     * search is suppressed, leaving the user with a populated form and a
     * disabled Search button explaining why.
     */
    if (meetsSearchCriteria(saved)) {
      setTriggerSearch((prev) => prev + 1);
    }
  }, []);

  /**
   * Resets every narrowing control at once: all filter dropdowns, the
   * exact-match toggle, and the free-text query. Search MODE and the sort order
   * are presentation choices rather than filters, so they are preserved.
   */
  const handleClearFilters = useCallback(() => {
    setSearchState((prev) => ({
      ...prev,
      queryText: '',
      exactMatch: false,
      filters: NO_FILTERS,
    }));
  }, []);

  const filtersActive = useMemo(
    () =>
      hasActiveFilters(
        searchState.filters,
        searchState.queryText,
        searchState.exactMatch,
      ),
    [searchState.filters, searchState.queryText, searchState.exactMatch],
  );

  /*
   * Note the deliberate asymmetry with `filtersActive` above: toggling only
   * exact match enables "Clear filters" but must NOT enable "Search". See the
   * two predicates in `searchFilterUtils.ts`.
   */
  const canSearch = useMemo(
    () => meetsSearchCriteria(searchState),
    [searchState],
  );

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          info={<InfoTooltip content={SEARCH_HELP_TEXT} />}
          description="Search across all strings and translations by ID, text, locale, or status."
        >
          Search Strings
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Alert type="info" statusIconAriaLabel="Info">
          Results are restricted to namespaces you have access to. If you need
          access to additional namespaces, contact the namespace owner via
          Bindle.
        </Alert>

        {/* Panel 1 — everything that narrows the search */}
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <Button onClick={handleClearFilters} disabled={!filtersActive}>
                  Clear filters
                </Button>
              }
            >
              Filters
            </Header>
          }
        >
          <SpaceBetween size="m">
            <SearchTypeToggle
              mode={searchState.mode}
              exactMatch={searchState.exactMatch}
              onModeChange={handleModeChange}
              onExactMatchChange={handleExactMatchChange}
            />

            <SearchBar
              queryText={searchState.queryText}
              onQueryChange={handleQueryChange}
              onSearch={handleSearch}
              currentSearchState={searchState}
              canSearch={canSearch}
            />

            {/*
              Saved searches sit directly BELOW the search input row and ABOVE
              the filter dropdowns. Render order inside this panel is therefore:
              SearchTypeToggle → SearchBar → SavedSearchesBar → SearchFilterBar.
              Note this component returns null when nothing is saved, so the row
              is absent (not empty) on a first visit.
            */}
            <SavedSearchesBar onLoadSearch={handleLoadSavedSearch} />

            <SearchFilterBar
              mode={searchState.mode}
              filters={searchState.filters}
              onFiltersChange={handleFiltersChange}
            />
          </SpaceBetween>
        </Container>

        {/*
         * Panel 2 — results. The table renders its own Cloudscape container
         * (`variant="container"` + `header`), which is the idiomatic way to give
         * a Table its own panel; wrapping it in an additional <Container> would
         * nest two containers and double the border/padding.
         */}
        <SearchResultsTable
          searchState={searchState}
          triggerSearch={triggerSearch}
          onSortChange={handleSortChange}
        />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default SearchPage;
